<template>
  <div id="payment-transactions" class="align-start fill-height">
    <i-overlay
      v-if="selectedLoading && $vuetifyDisplay.smAndDown"
      text="Loading payment transactions..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>
        <v-row>
          <v-spacer />
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="reports/payment-transactions"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedStatus === 'PAST' }"
              @click="setStatus('PAST', true)"
              >Past
            </v-btn>
          </v-col>
          <v-col cols="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedStatus === 'EXPECTED' }"
              @click="setStatus('EXPECTED', true)"
              >In Process
            </v-btn>
          </v-col>
          <v-col cols="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedStatus === 'PENDING' }"
              @click="setStatus('PENDING', true)"
              >Pending
            </v-btn>
          </v-col>
          <v-col cols="12">
            <i-local-filter
              :initialFilterText="filterText"
              :filterPlaceholder="`Search by Reference, Order Number, PO number, ${this.isILR ? 'Customer' : 'Retailer'} Type`"
              @filter="localFilter"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <template
              v-if="!selectedLoading && selectedTransactionGroups.length === 0"
            >
              <i-empty-results
                buttonText="Back to Reports"
                buttonRouteName="reports"
                :title="emptyFilteredResultsText"
              />
            </template>

            <template v-else>
              <template v-if="$vuetifyDisplay.smAndDown">
                <v-col
                  v-for="(item, index) in selectedTransactionGroups"
                  :key="index"
                  class="mt-0 pa-0"
                >
                  <v-card class="rounded-2 pa-4 mb-6">
                    <v-row no-gutters class="mb-3">
                      <template
                        v-for="row in getMobileGroupRows(item)"
                        :key="`${item.period?.identifier || item.objectId}-${row.value}`"
                      >
                        <v-col cols="6" class="py-1">
                          <span>{{ row.label }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <template v-if="row.type === 'currency'">
                            <i-currency-display
                              :dollarValue="row.value"
                            ></i-currency-display>
                          </template>
                          <template v-else>
                            <span
                              ><strong>{{ row.value }}</strong></span
                            >
                          </template>
                        </v-col>
                      </template>
                    </v-row>
                    <div class="text-center pt-2">
                      <v-btn
                        @click="exportExcel(item)"
                        class="i-btn-block-primary g-full-width"
                        color="primary"
                        :disabled="downloadingExcelPeriod !== null"
                        :loading="downloadingExcelPeriod === item.period"
                        v-if="selectedStatus === 'PAST'"
                      >
                        <span>Export Excel</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-excel-outline</v-icon
                        >
                      </v-btn>
                    </div>
                    <v-btn
                      rounded
                      class="g-full-width i-btn-block-primary mt-3 mb-4"
                      @click="item.showDetails = !item.showDetails"
                    >
                      <template v-if="item.showDetails">
                        <v-icon left class="p-icon">mdi-arrow-up</v-icon>
                        Hide details
                      </template>
                      <template v-else>
                        <v-icon left class="p-icon">mdi-arrow-down</v-icon>
                        Show details
                      </template>
                    </v-btn>
                    <span v-if="item.showDetails">
                      <v-row
                        v-for="transaction in item.transactions"
                        :key="transaction.objectId"
                      >
                        <v-card
                          class="rounded-2 pa-4 mb-6"
                          style="background-color: #f3f3f3"
                        >
                          <v-row no-gutters>
                            <template
                              v-for="row in getMobileTransactionRows(
                                transaction
                              )"
                              :key="`${transaction.objectId}-${row.key}`"
                            >
                              <v-col cols="6" class="py-1">
                                <span>{{ row.label }}</span>
                              </v-col>
                              <v-col cols="6" class="py-1 pl-4">
                                <template v-if="row.type === 'link'">
                                  <router-link
                                    v-if="createLinkForTransaction(transaction)"
                                    class="i-a-link"
                                    :to="createLinkForTransaction(transaction)"
                                  >
                                    <span>{{ $filters.text(row.value) }}</span>
                                  </router-link>
                                  <span v-else>{{
                                    $filters.text(row.value)
                                  }}</span>
                                </template>
                                <template v-else-if="row.type === 'currency'">
                                  <i-currency-display
                                    :dollarValue="row.value"
                                    :strong="false"
                                  ></i-currency-display>
                                </template>
                                <template v-else-if="row.type === 'payout'">
                                  <div class="payout-date-cell">
                                    <span>{{
                                      $filters.formatDate(row.value)
                                    }}</span>
                                    <span
                                      v-if="row.status"
                                      class="payout-date-status"
                                    >
                                      {{ row.status }}
                                    </span>
                                  </div>
                                </template>
                                <template v-else>
                                  <span>{{ $filters.text(row.value) }}</span>
                                </template>
                              </v-col>
                            </template>
                          </v-row>
                        </v-card>
                      </v-row>
                    </span>
                  </v-card>
                </v-col>
              </template>

              <template v-else>
                <v-data-table
                  class="i-data-table pt-2 elevation-1"
                  item-key="period.endDate"
                  show-expand
                  :expanded="expandedTransactionItems"
                  :single-expand="!expandAll"
                  :headers="headers"
                  :items="selectedTransactionGroups"
                  :loading="selectedLoading"
                  :items-per-page="-1"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                  hide-default-footer
                >
                  <template #[`item.period.identifier`]="{ item }">
                    <span>
                      <strong>
                        <template v-if="item.period">{{
                          $filters.text(item.period.identifier)
                        }}</template>
                        <template v-else>-</template>
                      </strong>
                    </span>
                  </template>

                  <template #[`item.period.startDate`]="{ item }">
                    <span>
                      <strong>
                        <template v-if="item.period">{{
                          $filters.formatDateTime12h(item.period.startDate)
                        }}</template>
                        <template v-else>-</template>
                      </strong>
                    </span>
                  </template>

                  <template #[`item.period.endDate`]="{ item }">
                    <span>
                      <strong>
                        <template v-if="item.period">{{
                          $filters.formatDateTime12h(item.period.endDate)
                        }}</template>
                        <template v-else>-</template>
                      </strong>
                    </span>
                  </template>

                  <template #[`item.totalSupplierPayoutTotalExTax`]="{ item }">
                    <i-currency-display
                      :dollarValue="item.totalSupplierPayoutTotalExTax"
                    ></i-currency-display>
                  </template>

                  <template #[`item.totalSupplierPayoutTotalWet`]="{ item }">
                    <i-currency-display
                      :dollarValue="item.totalSupplierPayoutTotalWet"
                    ></i-currency-display>
                  </template>

                  <template #[`item.totalSupplierPayoutTotalGst`]="{ item }">
                    <i-currency-display
                      :dollarValue="item.totalSupplierPayoutTotalGst"
                    ></i-currency-display>
                  </template>

                  <template #[`item.totalSupplierPayoutTotalIncTax`]="{ item }">
                    <i-currency-display
                      :dollarValue="item.totalSupplierPayoutTotalIncTax"
                    ></i-currency-display>
                  </template>

                  <template #[`item.payoutDate`]="{ item }">
                    <span
                      ><strong>{{
                        $filters.formatDate(item.payoutDate)
                      }}</strong></span
                    >
                  </template>

                  <template
                    #[`item.exportExcel`]="{ item }"
                    v-if="selectedStatus === 'PAST'"
                  >
                    <v-btn
                      @click="exportExcel(item)"
                      color="primary"
                      :disabled="downloadingExcelPeriod !== null"
                      :loading="downloadingExcelPeriod === item.period"
                      v-if="selectedStatus === 'PAST'"
                    >
                      <span>Excel</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-file-excel-outline</v-icon
                      >
                    </v-btn>
                  </template>

                  <template v-slot:expanded-item="{ headers, item }">
                    <td
                      :colspan="headers.length"
                      style="background-color: #f3f3f3"
                    >
                      <i-payment-transaction-details
                        :transactions="item.transactions"
                        :headers="transactionHeaders"
                        :create-link-for-transaction="createLinkForTransaction"
                      />
                    </td>
                  </template>
                </v-data-table>
              </template>
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import {
  fetchExpectedTransactionGroups,
  fetchPendingTransactionGroups,
  fetchProcessedTransactionGroups,
  fetchProcessedTransactionsExcel
} from '@/services/paymentTransactions'
import iOverlay from '@/components/Overlay'
import iLocalFilter from '@/components/LocalFilter'
import iPaymentTransactionDetails from '../components/PaymentTransactionDetails.vue'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import { replacePaymentPeriodHeader } from '@/utils/common'
import moment from 'moment-timezone'
import iCurrencyDisplay from '@/components/CurrencyDisplay.vue'

const OPTIONAL_TAX_HEADER = '__OPTIONAL_TAX_HEADER__'
const PRIMARY_TAX_HEADER = '__PRIMARY_TAX_HEADER__'

export default {
  name: 'payment-transactions',

  components: {
    iCurrencyDisplay,
    iEmptyResults,
    iInstructions,
    iOverlay,
    iLocalFilter,
    iPaymentTransactionDetails
  },

  data: () => ({
    commonHeaders: [
      {
        title: 'PAYMENT TRANSACTION PERIOD',
        key: 'period.identifier',
        text: 'PAYMENT TRANSACTION PERIOD',
        value: 'period.identifier'
      },
      {
        title: 'PERIOD START DATE',
        key: 'period.startDate',
        text: 'PERIOD START DATE',
        value: 'period.startDate'
      },
      {
        title: 'PERIOD END DATE',
        key: 'period.endDate',
        text: 'PERIOD END DATE',
        value: 'period.endDate'
      },
      {
        title: 'TOTAL (EX TAX)',
        key: 'totalSupplierPayoutTotalExTax',
        text: 'TOTAL (EX TAX)',
        value: 'totalSupplierPayoutTotalExTax'
      },
      {
        title: OPTIONAL_TAX_HEADER,
        key: 'totalSupplierPayoutTotalWet',
        text: OPTIONAL_TAX_HEADER,
        value: 'totalSupplierPayoutTotalWet'
      },
      {
        title: PRIMARY_TAX_HEADER,
        key: 'totalSupplierPayoutTotalGst',
        text: PRIMARY_TAX_HEADER,
        value: 'totalSupplierPayoutTotalGst'
      },
      {
        title: 'TOTAL (INC TAX)',
        key: 'totalSupplierPayoutTotalIncTax',
        text: 'TOTAL (INC TAX)',
        value: 'totalSupplierPayoutTotalIncTax'
      },
      {
        title: 'PAYOUT DATE',
        key: 'payoutDate',
        text: 'PAYOUT DATE',
        value: 'payoutDate'
      }
    ],

    loadingProcessed: true,
    loadingExpected: true,
    loadingPending: true,
    expandAll: false,
    processedTransactionGroups: [],
    expectedTransactionGroups: [],
    pendingTransactionGroups: [],
    originalProcessedTransactionGroups: [],
    originalExpectedTransactionGroups: [],
    originalPendingTransactionGroups: [],
    selectedStatus: 'PAST',
    filterText: '',
    downloadingExcelPeriod: null
  }),

  computed: {
    ...mapGetters('profile', [
      'bankDetailsAvailable',
      'processesOrdersOffline'
    ]),
    ...mapGetters('tenant', [
      'featureBankAccountsEnabled',
      'featurePaymentServicesPaymentEnabled',
      'featureCreditCardPaymentEnabled',
      'tenantPaymentTransactionsConfig',
      'isILR',
      'usesAustralianTaxLabels',
      'taxDisplayPolicy'
    ]),

    canAccessPaymentServices() {
      return (
        this.featurePaymentServicesPaymentEnabled &&
        !this.processesOrdersOffline
      )
    },

    canAccessCardServices() {
      return (
        this.featureCreditCardPaymentEnabled &&
        !this.processesOrdersOffline &&
        (!this.featureBankAccountsEnabled || this.bankDetailsAvailable)
      )
    },

    showGroupPayoutDate() {
      return this.selectedPaymentType !== 'CREDIT_CARD'
    },

    showTransactionPayout() {
      return this.selectedPaymentType === 'CREDIT_CARD'
    },

    selectedPaymentType() {
      return this.$route.meta.paymentType || 'PAYMENT_SERVICES'
    },
    transactionHeaders() {
      const headers = [
        {
          title: 'TYPE',
          key: 'displayType',
          text: 'TYPE',
          value: 'displayType',
          sortable: true
        },
        {
          title: 'REFERENCE',
          key: 'referenceNumber',
          text: 'REFERENCE',
          value: 'referenceNumber',
          sortable: true
        },
        {
          title: 'ORDER NUMBER',
          key: 'storeOrderReference',
          text: 'ORDER NUMBER',
          value: 'storeOrderReference',
          sortable: true
        },
        {
          title: 'SUPPLIER PO NUMBER',
          key: 'supplierOrderNumber',
          text: 'SUPPLIER PO NUMBER',
          value: 'supplierOrderNumber',
          sortable: true
        },
        {
          title: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          key: 'storeName',
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'storeName',
          sortable: true
        },
        {
          title: 'TOTAL (EX TAX)',
          key: 'supplierPayoutTotalExTax',
          text: 'TOTAL (EX TAX)',
          value: 'supplierPayoutTotalExTax',
          sortable: false
        },
        {
          title: OPTIONAL_TAX_HEADER,
          key: 'supplierPayoutTotalWet',
          text: OPTIONAL_TAX_HEADER,
          value: 'supplierPayoutTotalWet',
          sortable: false
        },
        {
          title: PRIMARY_TAX_HEADER,
          key: 'supplierPayoutTotalGst',
          text: PRIMARY_TAX_HEADER,
          value: 'supplierPayoutTotalGst',
          sortable: false
        },
        {
          title: 'TOTAL (INC TAX)',
          key: 'supplierPayoutTotalIncTax',
          text: 'TOTAL (INC TAX)',
          value: 'supplierPayoutTotalIncTax',
          sortable: false
        }
      ]

      if (this.showTransactionPayout) {
        headers.push({
          title: 'PAYOUT',
          key: 'payoutDisplayDate',
          text: 'PAYOUT',
          value: 'payoutDisplayDate',
          sortable: false
        })
      }

      return this.applyTaxDisplayPolicy(headers)
    },

    mobileSummaryHeaders() {
      return this.showGroupPayoutDate
        ? this.headers
        : this.headers.filter((header) => header.key !== 'payoutDate')
    },

    emptyFilteredResultsText() {
      return `No Transactions found`
    },

    headers() {
      const baseHeaders = this.showGroupPayoutDate
        ? this.commonHeaders
        : this.commonHeaders.filter((header) => header.key !== 'payoutDate')

      if (this.selectedStatus === 'PAST') {
        return this.applyTaxDisplayPolicy([
          ...baseHeaders,
          {
            title: 'EXPORT',
            key: 'exportExcel',
            text: 'EXPORT',
            value: 'exportExcel',
            width: '20',
            sortable: false
          }
        ])
      }
      if (this.selectedStatus === 'EXPECTED') {
        return this.applyTaxDisplayPolicy(baseHeaders)
      }
      if (this.selectedStatus === 'PENDING') {
        return this.applyTaxDisplayPolicy(baseHeaders)
      }
      return []
    },

    selectedLoading() {
      switch (this.selectedStatus) {
        case 'PAST':
          return this.loadingProcessed

        case 'EXPECTED':
          return this.loadingExpected

        case 'PENDING':
          return this.loadingPending
      }
      return this.loadingProcessed
    },

    selectedTransactionGroups() {
      let transactionGroups = []
      let originalTransactionGroups = []
      switch (this.selectedStatus) {
        case 'PAST':
          transactionGroups = this.processedTransactionGroups
          originalTransactionGroups = this.originalProcessedTransactionGroups
          break
        case 'EXPECTED':
          transactionGroups = this.expectedTransactionGroups
          originalTransactionGroups = this.originalExpectedTransactionGroups
          break

        case 'PENDING':
          transactionGroups = this.pendingTransactionGroups
          originalTransactionGroups = this.originalPendingTransactionGroups
          break
      }

      // there could have been a search happened before therefore we need to make sure we restore the original transactions
      transactionGroups.forEach((transactionGroup, index) => {
        transactionGroup.transactions =
          originalTransactionGroups[index].transactions
      })

      if (this.filterText.length > 0) {
        transactionGroups.forEach((transactionGroup) => {
          transactionGroup.transactions = transactionGroup.transactions.filter(
            (transaction) => {
              const filterTextLowerCase = this.filterText.toLowerCase().trim()
              return (
                transaction.referenceNumber
                  .toLowerCase()
                  .includes(filterTextLowerCase) ||
                transaction.storeName
                  ?.toLowerCase()
                  .includes(filterTextLowerCase) ||
                transaction.supplierOrderNumber
                  ?.toLowerCase()
                  .includes(filterTextLowerCase) ||
                transaction.storeOrderReference
                  ?.toLowerCase()
                  .includes(filterTextLowerCase) ||
                transaction.displayType
                  .toLowerCase()
                  .includes(filterTextLowerCase)
              )
            }
          )
        })
        return transactionGroups.filter(
          (transactionGroup) => transactionGroup.transactions.length > 0
        )
      } else {
        return transactionGroups
      }
    },

    expandedTransactionItems() {
      return this.expandAll ? this.selectedTransactionGroups : []
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    applyTaxDisplayPolicy(headers) {
      const taxDisplayPolicy = this.taxDisplayPolicy || {
        showWet: true,
        primaryTaxLabel: 'Sales Tax',
        wetLabel: 'Optional Tax'
      }
      const getHeaderKey = (header) => header.key || header.value

      return headers
        .filter((header) => {
          const headerKey = getHeaderKey(header)
          if (
            !this.usesAustralianTaxLabels &&
            [
              'totalSupplierPayoutTotalWet',
              'supplierPayoutTotalWet',
              'totalSupplierPayoutTotalGst',
              'supplierPayoutTotalGst',
              'totalSupplierPayoutTotalIncTax',
              'supplierPayoutTotalIncTax'
            ].includes(headerKey)
          ) {
            return false
          }
          if (
            !taxDisplayPolicy.showWet &&
            ['totalSupplierPayoutTotalWet', 'supplierPayoutTotalWet'].includes(
              headerKey
            )
          ) {
            return false
          }
          return true
        })
        .map((header) => {
          const headerKey = getHeaderKey(header)
          if (
            ['totalSupplierPayoutTotalWet', 'supplierPayoutTotalWet'].includes(
              headerKey
            )
          ) {
            return {
              ...header,
              title: taxDisplayPolicy.wetLabel.toUpperCase(),
              text: taxDisplayPolicy.wetLabel.toUpperCase()
            }
          }
          if (
            ['totalSupplierPayoutTotalGst', 'supplierPayoutTotalGst'].includes(
              headerKey
            )
          ) {
            return {
              ...header,
              title: taxDisplayPolicy.primaryTaxLabel.toUpperCase(),
              text: taxDisplayPolicy.primaryTaxLabel.toUpperCase()
            }
          }
          return header
        })
    },

    findHeaderText(headers, value, fallbackText) {
      return (
        headers.find((header) => header.key === value)?.title || fallbackText
      )
    },

    getMobileGroupRows(item) {
      const rows = []

      if (item.period) {
        rows.push(
          {
            label: this.findHeaderText(
              this.mobileSummaryHeaders,
              'period.identifier',
              'PAYMENT TRANSACTION PERIOD'
            ),
            value: item.period.identifier,
            type: 'text'
          },
          {
            label: this.findHeaderText(
              this.mobileSummaryHeaders,
              'period.startDate',
              'PERIOD START DATE'
            ),
            value: this.$options.filters.formatDateTime12h(
              item.period.startDate
            ),
            type: 'text'
          },
          {
            label: this.findHeaderText(
              this.mobileSummaryHeaders,
              'period.endDate',
              'PERIOD END DATE'
            ),
            value: this.$options.filters.formatDateTime12h(item.period.endDate),
            type: 'text'
          }
        )
      }

      rows.push({
        label: this.findHeaderText(
          this.mobileSummaryHeaders,
          'totalSupplierPayoutTotalExTax',
          'TOTAL (EX TAX)'
        ),
        value: item.totalSupplierPayoutTotalExTax,
        type: 'currency'
      })

      if (this.usesAustralianTaxLabels && this.taxDisplayPolicy?.showWet) {
        rows.push({
          label: this.findHeaderText(
            this.mobileSummaryHeaders,
            'totalSupplierPayoutTotalWet',
            this.taxDisplayPolicy?.wetLabel?.toUpperCase() || 'OPTIONAL TAX'
          ),
          value: item.totalSupplierPayoutTotalWet,
          type: 'currency'
        })
      }

      if (this.usesAustralianTaxLabels) {
        rows.push({
          label: this.findHeaderText(
            this.mobileSummaryHeaders,
            'totalSupplierPayoutTotalGst',
            this.taxDisplayPolicy?.primaryTaxLabel?.toUpperCase() ||
              'PRIMARY TAX'
          ),
          value: item.totalSupplierPayoutTotalGst,
          type: 'currency'
        })
      }

      if (this.usesAustralianTaxLabels) {
        rows.push({
          label: this.findHeaderText(
            this.mobileSummaryHeaders,
            'totalSupplierPayoutTotalIncTax',
            'TOTAL (INC TAX)'
          ),
          value: item.totalSupplierPayoutTotalIncTax,
          type: 'currency'
        })
      }

      if (this.showGroupPayoutDate) {
        rows.push({
          label: this.findHeaderText(
            this.mobileSummaryHeaders,
            'payoutDate',
            'PAYOUT DATE'
          ),
          value: this.$options.filters.formatDate(item.payoutDate),
          type: 'text'
        })
      }

      return rows
    },

    getMobileTransactionRows(transaction) {
      const rows = [
        {
          key: 'displayType',
          label: this.findHeaderText(
            this.transactionHeaders,
            'displayType',
            'TYPE'
          ),
          value: transaction.displayType,
          type: 'text'
        },
        {
          key: 'referenceNumber',
          label: this.findHeaderText(
            this.transactionHeaders,
            'referenceNumber',
            'REFERENCE'
          ),
          value: transaction.referenceNumber,
          type: 'link'
        }
      ]

      if (transaction.storeOrderReference) {
        rows.push({
          key: 'storeOrderReference',
          label: this.findHeaderText(
            this.transactionHeaders,
            'storeOrderReference',
            'ORDER NUMBER'
          ),
          value: transaction.storeOrderReference,
          type: 'text'
        })
      }

      if (
        transaction.supplierOrderNumber &&
        transaction.type !== 'SUPPLIER_ORDER'
      ) {
        rows.push({
          key: 'supplierOrderNumber',
          label: this.findHeaderText(
            this.transactionHeaders,
            'supplierOrderNumber',
            'SUPPLIER PO NUMBER'
          ),
          value: transaction.supplierOrderNumber,
          type: 'text'
        })
      }

      rows.push(
        {
          key: 'storeName',
          label: this.findHeaderText(
            this.transactionHeaders,
            'storeName',
            `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`
          ),
          value: transaction.storeName,
          type: 'text'
        },
        {
          key: 'supplierPayoutTotalExTax',
          label: this.findHeaderText(
            this.transactionHeaders,
            'supplierPayoutTotalExTax',
            'TOTAL (EX TAX)'
          ),
          value: transaction.supplierPayoutTotalExTax,
          type: 'currency'
        }
      )

      if (this.usesAustralianTaxLabels && this.taxDisplayPolicy?.showWet) {
        rows.push({
          key: 'supplierPayoutTotalWet',
          label: this.findHeaderText(
            this.transactionHeaders,
            'supplierPayoutTotalWet',
            this.taxDisplayPolicy?.wetLabel?.toUpperCase() || 'OPTIONAL TAX'
          ),
          value: transaction.supplierPayoutTotalWet,
          type: 'currency'
        })
      }

      if (this.usesAustralianTaxLabels) {
        rows.push({
          key: 'supplierPayoutTotalGst',
          label: this.findHeaderText(
            this.transactionHeaders,
            'supplierPayoutTotalGst',
            this.taxDisplayPolicy?.primaryTaxLabel?.toUpperCase() ||
              'PRIMARY TAX'
          ),
          value: transaction.supplierPayoutTotalGst,
          type: 'currency'
        })
      }

      if (this.usesAustralianTaxLabels) {
        rows.push({
          key: 'supplierPayoutTotalIncTax',
          label: this.findHeaderText(
            this.transactionHeaders,
            'supplierPayoutTotalIncTax',
            'TOTAL (INC TAX)'
          ),
          value: transaction.supplierPayoutTotalIncTax,
          type: 'currency'
        })
      }

      if (this.showTransactionPayout) {
        rows.push({
          key: 'payoutDisplayDate',
          label: this.findHeaderText(
            this.transactionHeaders,
            'payoutDisplayDate',
            'PAYOUT'
          ),
          value: transaction.payoutDisplayDate,
          status: transaction.payoutDisplayStatus,
          type: 'payout'
        })
      }

      return rows
    },

    replacePaymentPeriodHeader() {
      replacePaymentPeriodHeader(
        this.commonHeaders,
        this.tenantPaymentTransactionsConfig
      )
    },

    localFilter(filterText) {
      this.expandAll = false
      this.processedTransactionGroups.forEach((e) => {
        e.showDetails = false
      })
      this.expectedTransactionGroups.forEach((e) => {
        e.showDetails = false
      })
      this.pendingTransactionGroups.forEach((e) => {
        e.showDetails = false
      })
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    mapToDisplayType(type) {
      switch (type) {
        case 'SUPPLIER_ORDER':
          return 'Supplier PO'

        case 'SUPPLIER_ORDER_QUERY':
          return 'Order Query'

        case 'CREDIT_RETURN':
          return 'Credit Return'

        case 'PAYMENT_ADJUSTMENT':
          return 'Adjustment'
      }
    },

    createLinkForTransaction(transaction) {
      const objectId = transaction.objectId.toString()
      switch (transaction.type) {
        case 'SUPPLIER_ORDER':
          return {
            name: 'purchase-order-details',
            params: { id: objectId }
          }

        case 'SUPPLIER_ORDER_QUERY':
          return {
            name: 'order-query-details',
            params: { id: objectId }
          }

        case 'CREDIT_RETURN':
          return {
            name: 'credit-return-details',
            params: { id: objectId }
          }

        case 'PAYMENT_ADJUSTMENT':
          return {
            name: 'payment-adjustment-details',
            params: { paymentAdjustmentId: objectId }
          }
      }
    },

    addFrontendFields(transactionGroups) {
      transactionGroups.forEach((transactionGroup) => {
        transactionGroup.showDetails = this.expandAll || false
        transactionGroup.transactions.forEach((transaction) => {
          transaction.displayType = this.mapToDisplayType(transaction.type)
          transaction.payoutDisplayDate =
            transaction.stripePayoutDate ||
            transaction.supplierPayoutDate ||
            null
          transaction.payoutDisplayStatus = transaction.stripePayoutDate
            ? 'Paid'
            : transaction.supplierPayoutDate
              ? 'Expected'
              : null
        })
      })
      return transactionGroups
    },

    setStatus(status, fetch) {
      this.selectedStatus = status
      if (fetch) {
        return this.fetchSelectedTransactions()
      }
    },

    fetchSelectedTransactions() {
      switch (this.selectedStatus) {
        case 'PAST':
          return this.fetchProcessed()
        case 'EXPECTED':
          return this.fetchExpected()
        case 'PENDING':
          return this.fetchPending()
      }
      return this.fetchProcessed()
    },

    fetchProcessed() {
      this.loadingProcessed = true
      return fetchProcessedTransactionGroups(this.selectedPaymentType)
        .then((res) => {
          this.processedTransactionGroups = this.addFrontendFields(res)
          this.originalProcessedTransactionGroups = _.cloneDeep(
            this.processedTransactionGroups
          )
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Processed transactions could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loadingProcessed = false
        })
    },

    fetchExpected() {
      this.loadingExpected = true
      return fetchExpectedTransactionGroups(this.selectedPaymentType)
        .then((res) => {
          this.expectedTransactionGroups = this.addFrontendFields(res)
          this.originalExpectedTransactionGroups = _.cloneDeep(
            this.expectedTransactionGroups
          )
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'In Process transactions could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loadingExpected = false
        })
    },

    fetchPending() {
      this.loadingPending = true
      return fetchPendingTransactionGroups(this.selectedPaymentType)
        .then((res) => {
          this.pendingTransactionGroups = this.addFrontendFields(res)
          this.originalPendingTransactionGroups = _.cloneDeep(
            this.pendingTransactionGroups
          )
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Pending transactions could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loadingPending = false
        })
    },

    exportExcel(item) {
      const period = item.period
      this.downloadingExcelPeriod = period
      const endDateMoment = moment(period.endDate)
      const periodEndDateIso = endDateMoment.format()
      const periodEndDateForFileName = endDateMoment.format('YYYY_MM_DD')
      const reportLabel =
        this.selectedPaymentType === 'CREDIT_CARD'
          ? 'card_services'
          : 'payment_services'
      const fileName = `${reportLabel}_transactions_for_period_ending_${periodEndDateForFileName}.xlsx`
      fetchProcessedTransactionsExcel(
        periodEndDateIso,
        this.selectedPaymentType
      )
        .then((response) => {
          const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const link = document.createElement('a')

          link.href = window.URL.createObjectURL(blob)
          link.download = fileName

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Excel could not be exported, please try again...'
          })
        })
        .finally(() => {
          this.downloadingExcelPeriod = null
        })
    }
  },

  async mounted() {
    if (
      (this.selectedPaymentType === 'CREDIT_CARD' &&
        !this.canAccessCardServices) ||
      (this.selectedPaymentType === 'PAYMENT_SERVICES' &&
        !this.canAccessPaymentServices)
    ) {
      await this.$router.push({ name: 'dashboard' })
      return
    }

    this.replacePaymentPeriodHeader()

    // A Reference Number can be passed as a route query param, if so, we pre-populate the local search filter and
    // set all rows to be expanded initially
    if (this.$route.query.reference) {
      this.filterText = this.$route.query.reference
      this.expandAll = true
    }

    this.setStatus(this.$route.query.status || 'PAST', false)
    this.fetchSelectedTransactions()
  }
}
</script>

<style lang="scss" scoped>
#payment-transactions {
  .payout-date-cell {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .payout-date-status {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
    text-transform: uppercase;
  }
}
</style>
